import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Maximize2, Monitor, Smartphone } from "lucide-react";

type Props = {
    onPresentHere: () => void;
    onOpenWindow: () => void;
    /** The second-monitor window is open. */
    windowOpen: boolean;
    onShare: () => void;
    /** A link exists, and whether the patient's device is on it now. */
    remote: { shared: boolean; present: boolean };
};

type Status = { tone: "ok" | "idle"; label: string } | null;

function Output({
    icon,
    title,
    hint,
    status,
    onClick,
    className = "",
}: {
    icon: ReactNode;
    title: string;
    hint: string;
    status: Status;
    onClick: () => void;
    className?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex min-h-20 items-start gap-3 rounded-2xl border p-3 text-left transition-colors ${
                status?.tone === "ok"
                    ? "border-room-ok/40 bg-room-ok-tint hover:bg-room-hover"
                    : "border-room-line-soft bg-room-raised hover:bg-room-hover"
            } ${className}`}
        >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-room-active text-room-ink">
                {icon}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-room-ink">{title}</span>
                <span className="block text-xs text-room-ink-muted">{hint}</span>
                {status && (
                    <span
                        className={`mt-1 inline-flex items-center gap-1.5 text-xs font-medium ${
                            status.tone === "ok" ? "text-room-ok" : "text-room-ink-muted"
                        }`}
                    >
                        <span
                            aria-hidden="true"
                            className={`size-1.5 rounded-full ${status.tone === "ok" ? "bg-room-ok" : "bg-room-ink-muted"}`}
                        />
                        {status.label}
                    </span>
                )}
            </span>
        </button>
    );
}

/**
 * Where the patient sees the dot. Not a choice of one: a set can be on a
 * monitor facing them and on their phone at once, so each way in says on its
 * own whether it is live.
 */
export default function PatientOutputs({ onPresentHere, onOpenWindow, windowOpen, onShare, remote }: Props) {
    const { t } = useTranslation();

    return (
        <section aria-labelledby="emdr-outputs-title" className="flex flex-col gap-2">
            <h2 id="emdr-outputs-title" className="text-xs font-semibold uppercase tracking-wide text-room-ink-muted">
                {t("emdr.console.outputs.title")}
            </h2>
            <div className="grid gap-2 sm:grid-cols-3">
                <Output
                    icon={<Maximize2 size={18} aria-hidden="true" />}
                    title={t("emdr.console.outputs.here")}
                    hint={t("emdr.console.outputs.hereHint")}
                    status={null}
                    onClick={onPresentHere}
                />
                <Output
                    // A second window means a second screen; a phone has one.
                    className="hidden sm:flex"
                    icon={<Monitor size={18} aria-hidden="true" />}
                    title={t("emdr.console.outputs.window")}
                    hint={t("emdr.console.outputs.windowHint")}
                    status={windowOpen ? { tone: "ok", label: t("emdr.console.outputs.windowOpen") } : null}
                    onClick={onOpenWindow}
                />
                <Output
                    icon={<Smartphone size={18} aria-hidden="true" />}
                    title={t("emdr.console.outputs.device")}
                    hint={t("emdr.console.outputs.deviceHint")}
                    status={
                        remote.present
                            ? { tone: "ok", label: t("emdr.console.outputs.deviceConnected") }
                            : remote.shared
                              ? { tone: "idle", label: t("emdr.console.outputs.deviceWaiting") }
                              : null
                    }
                    onClick={onShare}
                />
            </div>
        </section>
    );
}
